import { useEffect, useRef } from "react";
import { X, type LucideIcon } from "lucide-react";
import { cn } from "@weco/ui";

import type { ShellTab } from "./tabs/tabModel";

export interface HeaderTabMeta {
  label: string;
  Icon: LucideIcon;
  isHub: boolean;
  /** True when the label is user content (a document title). */
  isDocTitle: boolean;
}

export interface HeaderTabsProps {
  tabs: ShellTab[];
  activeKey: string | null;
  onSelect: (key: string) => void;
  onClose: (key: string) => void;
  meta: (tab: ShellTab) => HeaderTabMeta;
  ariaLabel: string;
  closeLabel: string;
}

/**
 * The browser-style tab rail (design: `weco2/shell.jsx` HeaderTabs).
 *
 * The active tab is a sheet whose base flares into the content panel below
 * (`.btab-active` fillets, defined globally). Tabs flex down to a minimum
 * width, then the rail scrolls and its overflow fades (`.btab-rail`).
 */
export function HeaderTabs({ tabs, activeKey, onSelect, onClose, meta, ariaLabel, closeLabel }: HeaderTabsProps) {
  const railRef = useRef<HTMLDivElement>(null);

  // Keep the active tab in view on a crowded rail.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !activeKey) return;
    const el = Array.from(rail.querySelectorAll<HTMLElement>("[data-tab-key]")).find(
      (node) => node.dataset.tabKey === activeKey,
    );
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pr = rail.getBoundingClientRect();
    if (r.left < pr.left) rail.scrollLeft -= pr.left - r.left + 24;
    else if (r.right > pr.right) rail.scrollLeft += r.right - pr.right + 24;
  }, [activeKey, tabs.length]);

  return (
    <div
      ref={railRef}
      role="tablist"
      aria-label={ariaLabel}
      className="btab-rail flex items-end gap-1 flex-1 min-w-0 h-full overflow-x-auto no-scrollbar"
      style={{ marginInlineStart: -12 }}
    >
      {tabs.map((tab) => {
        const { label, Icon, isHub, isDocTitle } = meta(tab);
        const active = tab.key === activeKey;
        return (
          <div
            key={tab.key}
            data-tab-key={tab.key}
            data-tab-route={tab.routeId}
            data-i18n-skip={isDocTitle ? undefined : "true"}
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            title={label}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(tab.key);
              } else if (e.key === "Delete") {
                e.preventDefault();
                onClose(tab.key);
              } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                e.preventDefault();
                const els = Array.from(railRef.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []);
                const i = els.indexOf(e.currentTarget);
                const rtl = document.documentElement.dir === "rtl";
                const step = (e.key === "ArrowRight") !== rtl ? 1 : -1;
                els[i + step]?.focus();
              }
            }}
            onClick={() => onSelect(tab.key)}
            onMouseDown={(e) => {
              if (e.button === 1) {
                e.preventDefault();
                onClose(tab.key);
              }
            }}
            onAuxClick={(e) => {
              // Some browsers deliver the middle click here instead.
              if (e.button === 1) e.preventDefault();
            }}
            style={{
              flex: "1 1 0%",
              minWidth: "var(--tab-min, 112px)",
              maxWidth: "var(--tab-max, 196px)",
              paddingInlineStart: 12,
              paddingInlineEnd: 8,
              paddingTop: 0,
              paddingBottom: 0,
            }}
            className={cn("btab group", active && "btab-active")}
          >
            <Icon size={15} className={cn("shrink-0", active && isHub && "text-primary")} aria-hidden />
            <span className={cn("truncate flex-1 min-w-0 text-[13px]", active ? "font-semibold" : "font-medium")}>
              {label}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.key);
              }}
              title={closeLabel}
              aria-label={`${closeLabel}: ${label}`}
              tabIndex={-1}
              className={cn(
                "btab-close shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-all hover:bg-foreground/10",
                active ? "opacity-55 hover:opacity-100" : "opacity-0 group-hover:opacity-60 hover:!opacity-100",
              )}
            >
              <X size={12} strokeWidth={2.5} aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
